import React, { useCallback } from 'react';
import { Typography, Chip, Box } from '@mui/material';
import AdminLayout from '../../components/AdminLayout';
import AdminTable from '../../components/AdminTable';
import apiClient from '../../services/apiClient';

function formatDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' });
}

const EVENT_TYPES = [
  'login_success', 'login_failed', 'account_locked', 'logout',
  'password_change', 'password_add', 'email_verified', 'user_enabled', 'user_disabled',
  'admin_password_reset', 'admin_approve_user', 'admin_reject_user',
  'admin_force_logout', 'admin_account_unlock', 'admin_terminate_session',
  'app_access_granted', 'app_access_revoked',
];

export default function SecurityEvents() {
  const fetchEvents = useCallback(async (params) => {
    const qs = new URLSearchParams({
      limit: String(params.limit),
      offset: String(params.offset),
      sort_field: params.sort_field,
      sort_dir: params.sort_dir,
    });
    if (params.search) qs.set('search', params.search);
    if (params.eventType) qs.set('eventType', params.eventType);

    const data = await apiClient.get(`/api/admin/security-events?${qs}`);
    return { rows: data.events || [], total: data.total || data.pagination?.total || 0 };
  }, []);

  const columns = [
    {
      id: 'created_at', label: 'Time', minWidth: 150,
      render: (row) => formatDateTime(row.created_at),
    },
    {
      id: 'user_email', label: 'User', minWidth: 160,
      render: (row) => row.user_email || row.user_id?.substring(0, 8) || '—',
    },
    {
      id: 'event_type', label: 'Event', minWidth: 140,
      render: (row) => (
        <Chip
          label={row.event_type?.replace(/_/g, ' ')}
          size="small" variant="outlined" color="info"
          sx={{ fontSize: '0.7rem' }}
        />
      ),
    },
    {
      id: 'success', label: 'Success', minWidth: 80,
      render: (row) => (
        <Chip
          label={row.success ? 'Yes' : 'No'}
          size="small" variant="outlined"
          color={row.success ? 'success' : 'error'}
          sx={{ fontSize: '0.7rem' }}
        />
      ),
    },
    { id: 'ip_address', label: 'IP Address', minWidth: 120 },
    {
      id: 'failure_reason', label: 'Details', minWidth: 150, sortable: false,
      render: (row) => {
        const text = row.failure_reason || (row.details ? JSON.stringify(row.details).substring(0, 60) : '—');
        return (
          <Typography variant="caption" sx={{ fontSize: '0.7rem', maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {text}
          </Typography>
        );
      },
    },
  ];

  const filters = [
    {
      id: 'eventType', label: 'Event Type',
      options: EVENT_TYPES.map(t => ({ value: t, label: t.replace(/_/g, ' ') })),
    },
  ];

  return (
    <AdminLayout>
      <Typography variant="h4" sx={{ mb: 3, fontFamily: "'EB Garamond', serif" }}>
        Security Events
      </Typography>

      <AdminTable
        tableId="admin_security_events"
        columns={columns}
        fetchData={fetchEvents}
        filters={filters}
        defaultSort={{ field: 'created_at', dir: 'desc' }}
      />
    </AdminLayout>
  );
}
